import React, { useEffect, useState } from "react";

const TABS = [
  { label: "All FAQs", value: "all", icon: "❓" },
  { label: "General", value: "General", icon: "📋" },
  { label: "Technical", value: "Technical", icon: "🔧" },
  { label: "Billing", value: "Billing", icon: "💳" },
  { label: "Support", value: "Support", icon: "🎧" },
];

const categoryStyles = {
  General: "bg-blue-100 text-blue-800",
  Technical: "bg-purple-100 text-purple-800",
  Billing: "bg-green-100 text-green-800",
  Support: "bg-orange-100 text-orange-800",
  default: "bg-gray-100 text-gray-800",
};

const Faqs = () => {
  const [faqs, setFaqs] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [formData, setFormData] = useState({
    question: "",
    answer: "",
    category: "General"
  });

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/faqs/all-faqs`
        );
        const data = await response.json();
        setFaqs(data.faqs || data);
      } catch (error) {
        console.error("Error fetching FAQs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchFaqs();
  }, []);

  const deleteFaq = async (faqId) => {
    try {
      setDeletingId(faqId);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/faqs/delete-faq/${faqId}`,
        { method: 'DELETE' }
      );
      
      if (response.ok) {
        setFaqs((prev) =>
          prev.filter((faq) => faq._id !== faqId)
        );
      }
    } catch (error) {
      console.error("Error deleting FAQ:", error);
    } finally {
      setDeletingId(null);
    }
  };

  const handleDelete = async (faqId) => {
    if (window.confirm("Are you sure you want to delete this FAQ?")) {
      await deleteFaq(faqId);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      
      if (editingFaq) {
        // Update existing FAQ
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/faqs/update-faq/${editingFaq._id}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(formData),
          }
        );
        
        if (response.ok) {
          const updatedFaq = await response.json();
          setFaqs(prev => 
            prev.map(faq => 
              faq._id === editingFaq._id ? updatedFaq : faq
            )
          );
        }
      } else {
        // Create new FAQ
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/faqs/create-faq`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(formData),
          }
        );
        
        if (response.ok) {
          const newFaq = await response.json();
          setFaqs(prev => [newFaq, ...prev]);
        }
      }
      
      closeModal();
    } catch (error) {
      console.error("Error saving FAQ:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (faq) => {
    setEditingFaq(faq);
    setFormData({
      question: faq.question,
      answer: faq.answer,
      category: faq.category
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingFaq(null);
    setFormData({
      question: "",
      answer: "",
      category: "General"
    });
  };

  const toggleExpanded = (faqId) => {
    setExpandedFaq(expandedFaq === faqId ? null : faqId);
  };

  const filteredFaqs = faqs
    .filter((item) => {
      if (activeTab === "all") return true;
      return item.category === activeTab;
    })
    .filter(
      (item) =>
        item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const getCategoryStyle = (category) => {
    return categoryStyles[category] || categoryStyles.default;
  };

  const truncateText = (text, maxLength = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  return (
    <div className="min-h-screen p-6">
      <div className="">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-800">
              Frequently Asked Questions
            </h1>
            <p className="text-gray-500 mt-1">
              Manage and organize customer FAQs
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              Add FAQ
            </button>
            
            <div className="relative w-full md:w-64">
              <input
                type="text"
                placeholder="Search FAQs..."
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <svg
                className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-8">
          {TABS.map((tab) => {
            const count = faqs.filter((item) => {
              if (tab.value === "all") return true;
              return item.category === tab.value;
            }).length;
            
            return (
              <button
                key={tab.value}
                onClick={() => setActiveTab(tab.value)}
                className={`flex items-center px-5 py-3 rounded-xl font-medium transition-all duration-200 ${
                  activeTab === tab.value
                    ? "bg-blue-100 text-blue-800 shadow-inner transform scale-[1.02]"
                    : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
                }`}
              >
                <span className="mr-2 text-lg">{tab.icon}</span>
                {tab.label}
                <span
                  className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                    activeTab === tab.value ? "bg-white" : "bg-gray-100"
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          {TABS.map((tab) => {
            const count = faqs.filter((item) => {
              if (tab.value === "all") return true;
              return item.category === tab.value;
            }).length;
            
            return (
              <div
                key={`stat-${tab.value}`}
                className="bg-white p-4 rounded-xl shadow-sm border border-gray-100"
              >
                <div className="flex items-center">
                  <span className="text-2xl mr-3 p-2 rounded-full bg-blue-100">
                    {tab.icon}
                  </span>
                  <div>
                    <p className="text-sm text-gray-500">{tab.label}</p>
                    <p className="text-2xl font-bold">{count}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((_, idx) => (
              <div
                key={idx}
                className="p-6 border rounded-xl shadow-sm animate-pulse bg-white"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="space-y-2 flex-1">
                    <div className="h-4 w-3/4 bg-gray-300 rounded"></div>
                    <div className="h-3 w-16 bg-gray-200 rounded"></div>
                  </div>
                  <div className="h-8 w-16 bg-gray-200 rounded"></div>
                </div>
                <div className="space-y-2">
                  <div className="h-3 w-full bg-gray-200 rounded"></div>
                  <div className="h-3 w-2/3 bg-gray-200 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredFaqs.length === 0 ? (
          <div className="bg-white p-8 rounded-xl shadow-sm text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-gray-900">
              No FAQs found
            </h3>
            <p className="mt-1 text-gray-500">
              {searchTerm
                ? "Try a different search term"
                : "No FAQs available"}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredFaqs.map((faq) => (
              <div
                key={faq._id}
                className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 border border-gray-100"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-start gap-3 mb-2">
                        <span
                          className={`text-xs px-2 py-1 rounded-full flex-shrink-0 mt-1 ${getCategoryStyle(
                            faq.category
                          )}`}
                        >
                          {faq.category}
                        </span>
                      </div>
                      <h3 className="font-semibold text-gray-800 text-lg leading-relaxed">
                        {faq.question}
                      </h3>
                    </div>
                    
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => handleEdit(faq)}
                        className="text-gray-400 hover:text-blue-600 transition-colors p-1"
                        title="Edit FAQ"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                      </button>
                      
                      <button
                        onClick={() => handleDelete(faq._id)}
                        disabled={deletingId === faq._id}
                        className="text-gray-400 hover:text-red-600 transition-colors p-1"
                        title="Delete FAQ"
                      >
                        {deletingId === faq._id ? (
                          <svg
                            className="animate-spin h-4 w-4"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                        ) : (
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        )}
                      </button>
                      
                      <button
                        onClick={() => toggleExpanded(faq._id)}
                        className="text-gray-400 hover:text-blue-600 transition-colors p-1 ml-2"
                        title={expandedFaq === faq._id ? "Collapse" : "Expand"}
                      >
                        <svg
                          className={`w-5 h-5 transform transition-transform ${
                            expandedFaq === faq._id ? "rotate-180" : ""
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div className="text-gray-700">
                    {expandedFaq === faq._id ? (
                      <p className="leading-relaxed">{faq.answer}</p>
                    ) : (
                      <p className="leading-relaxed">
                        {truncateText(faq.answer)}
                        {faq.answer.length > 100 && (
                          <button
                            onClick={() => toggleExpanded(faq._id)}
                            className="text-blue-600 hover:text-blue-700 ml-1 font-medium"
                          >
                            Read more
                          </button>
                        )}
                      </p>
                    )}
                  </div>

                  {faq.createdAt && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <span className="text-xs text-gray-500">
                        Created: {new Date(faq.createdAt).toLocaleDateString()}
                        {faq.updatedAt && faq.updatedAt !== faq.createdAt && (
                          <span className="ml-4">
                            Updated: {new Date(faq.updatedAt).toLocaleDateString()}
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add/Edit FAQ Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-800">
                    {editingFaq ? "Edit FAQ" : "Add New FAQ"}
                  </h2>
                  <button
                    onClick={closeModal}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                      Category *
                    </label>
                    <select
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="General">General</option>
                      <option value="Technical">Technical</option>
                      <option value="Billing">Billing</option>
                      <option value="Support">Support</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="question" className="block text-sm font-medium text-gray-700 mb-1">
                      Question *
                    </label>
                    <input
                      type="text"
                      id="question"
                      name="question"
                      value={formData.question}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter the frequently asked question..."
                    />
                  </div>

                  <div>
                    <label htmlFor="answer" className="block text-sm font-medium text-gray-700 mb-1">
                      Answer *
                    </label>
                    <textarea
                      id="answer"
                      name="answer"
                      value={formData.answer}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Provide a comprehensive answer to the question..."
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="flex-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={submitting}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
                    >
                      {submitting ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          {editingFaq ? "Updating..." : "Adding..."}
                        </>
                      ) : (
                        editingFaq ? "Update FAQ" : "Add FAQ"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Faqs;